/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import DataTable from "components/Tables/DataTable";
import CustomCell from "pages/ManageContracts/Single/ContractCard/CustomCell";
import FormulaBlock from "pages/ManageContracts/Single/ContractCard/FormulaBlock";
import InputDefault from "components/InputFields/InputDefault";
import { Card, Typography, Box } from "@mui/material";

// Styles
import "./custom.styles.css";

/**
 * Maps backend field names to translation keys and display order.
 */
const tariffFields = [
  { field: "peak_value", periodKey: "PONTA" },
  { field: "mid_peak_value", periodKey: "CHEIAS" },
  { field: "off_peak_value", periodKey: "VAZIO_NORMAL" },
  { field: "super_off_peak_value", periodKey: "SUPER_VAZIO" },
];

function useColumns(t) {
  return [
    { Header: t("single.tariffs.table.columns.name"), accessor: "type", width: "60%" },
    { Header: t("single.tariffs.table.columns.value"), accessor: "value", width: "40%" },
  ];
}

function Tariffs({ data, action, onChange }) {
  const { t } = useTranslation(["contracts"]);
  const defaultColumns = useColumns(t);

  const rows = tariffFields.map(({ field, periodKey }) => ({
    type: t(`single.fields.periods.${periodKey}`),
    value: CustomCell({
      value: data?.[field] ?? 0,
      unit: "€/kWh",
      disabled: action === "view",
      onChange: (e) => onChange(field, e.target.value),
      reference: field,
    }),
  }));

  return (
    <Card
      sx={{
        p: 2,
        border: (theme) =>
          theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "none",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : undefined,
      }}
    >
      <DataTable
        table={{
          columns: defaultColumns,
          rows,
        }}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
      />

      <Box mt={3} mb={2}>
        <Typography variant="h6" sx={{ marginBottom: 2, color: "text.primary", fontWeight: 600 }}>
          {t("single.fixed.multipliers_title")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ maxWidth: 160 }}>
            <InputDefault
              disabled={action === "view"}
              type="number"
              size="small"
              label={t("single.fixed.multiplierPMD")}
              value={data?.multiplierPMD ?? 1}
              inputProps={{ step: "any" }}
              onChange={(e) => onChange("multiplierPMD", e.target.value)}
            />
          </Box>
          <Box sx={{ maxWidth: 160 }}>
            <InputDefault
              disabled={action === "view"}
              type="number"
              size="small"
              label={t("single.fixed.multiplierERSE")}
              value={data?.multiplierERSE ?? 1}
              inputProps={{ step: "any" }}
              onChange={(e) => onChange("multiplierERSE", e.target.value)}
            />
          </Box>
        </Box>
      </Box>

      <FormulaBlock
        title={t("single.fixed.formula.title")}
        body={`${t("single.fixed.formula.body_buy")}\n${t("single.fixed.formula.body_sell")}`}
      />
    </Card>
  );
}

Tariffs.defaultProps = {
  onChange: null,
};

Tariffs.propTypes = {
  data: PropTypes.shape({
    peak_value: PropTypes.number,
    mid_peak_value: PropTypes.number,
    off_peak_value: PropTypes.number,
    super_off_peak_value: PropTypes.number,
    multiplierPMD: PropTypes.number,
    multiplierERSE: PropTypes.number,
  }).isRequired,
  action: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};

export default Tariffs;
